import type { EventMapFromUnion } from '@pkg/server/events'
import { isSSR } from '@pkg/shared/env'
import type { Emitter } from 'nanoevents'
import { Tab } from 'tab-election'

export interface CrossTabEventsOpts {
  autoStart?: boolean
}

/**
 * Creates a cross-tab synchronized event system.
 * Events emitted in one tab will be broadcast to all other tabs.
 *
 * @param namespace - Unique namespace for tab-election channel
 * @param events - Optional existing nanoevents emitter to wrap
 * @param opts
 * @returns Enhanced events object with cross-tab sync
 */
export function createCrossTabEvents<TEventUnion extends { type: string }>(
  namespace: string,
  events: Emitter<EventMapFromUnion<TEventUnion>>,
  opts: CrossTabEventsOpts = {},
): {
  events: Emitter<EventMapFromUnion<TEventUnion>>
  emit: <TType extends TEventUnion['type']>(
    type: TType,
    payload: Omit<Extract<TEventUnion, { type: TType }>, 'type'>,
  ) => void
  start: () => void
  stop: () => void
  isLeader: () => boolean
} {
  type TMap = EventMapFromUnion<TEventUnion>

  if (isSSR) {
    return {
      events,
      emit: () => {},
      start: () => {},
      stop: () => {},
      isLeader: () => false,
    }
  }

  const { autoStart = true } = opts

  const localEvents = events
  const tab = new Tab<Record<string, never>>(`${namespace}:events`)

  let started = false
  let isLeaderState = false

  type BroadcastMessage = {
    type: 'event'
    eventType: string
    payload: unknown
  }

  const onMessage = (event: MessageEvent) => {
    const msg = event.data as BroadcastMessage
    if (msg?.type === 'event') {
      // Re-emit received events locally
      // @ts-expect-error it has to be an object
      const fullEvent = { type: msg.eventType, ...msg.payload } as Extract<TEventUnion, { type: typeof msg.eventType }>
      // @ts-expect-error nanoevents typing limitation
      localEvents.emit(msg.eventType as keyof TMap, fullEvent as never)
    }
  }

  const emit = <TType extends TEventUnion['type']>(
    type: TType,
    payload: Omit<Extract<TEventUnion, { type: TType }>, 'type'>,
  ) => {
    const event = { type, ...payload } as Extract<TEventUnion, { type: TType }>

    // Emit locally
    // @ts-expect-error nanoevents typing limitation
    localEvents.emit(type as keyof TMap, event as never)

    // Broadcast to other tabs if started
    if (started) {
      tab.send({
        type: 'event',
        eventType: type,
        payload,
      } satisfies BroadcastMessage)
    }
  }

  const start = () => {
    if (started) return
    started = true

    tab.addEventListener('message', onMessage)

    void tab.waitForLeadership(() => {
      isLeaderState = true
      return {}
    })
  }

  const stop = () => {
    if (!started) return
    started = false
    isLeaderState = false

    tab.removeEventListener('message', onMessage)
    tab.close()
  }

  if (autoStart) {
    start()
  }

  return {
    events: localEvents,
    emit,
    start,
    stop,
    isLeader: () => isLeaderState,
  }
}
